import { type MerchantDetailDto, ProductCode } from "Infrastructure/Api/Api";
import { OrbitLContractDownloads } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/OrbitLContractDownloads";
import { OrbitLFacilityTerminals } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/OrbitLFacilityTerminals";
import { OrbitLProgress } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/OrbitLProgress";
import { ProductFeeUpdateForm } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/ProductFeeUpdateForm";
import type { ProductPricingVariants } from "Components/Hunter/Merchants/types";
import { DeactivateProductButton } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/DeactivateProductButton";
import { CurrentPricingFee } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/CurrentPricingFee";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { useGetOrbitLStatusQuery } from "Api/Queries/Merchants/useGetOrbitLStatusQuery";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { isComgateProductCode } from "Components/Shared/Merchant/Products/AppProducts/constants";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { Box } from "@mui/material";

import type { AppProductsVariant } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";
import { useCurrentUserAccessRole } from "Hooks/useCurrentUserAccessRole";
import { isHunter } from "Hooks/useIsCurrentUserHunter";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import {
  canManageOrbitLTerminals,
  isBeforeComgateSubmission,
} from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/orbitLTerminalManagement";

const ORBIT_L_CONTRACT_DOWNLOAD_ROLES = [
  AccessRoleCodes.Merchant,
  AccessRoleCodes.Admin,
] as const;

type ProductDetailProps = {
  code: ProductCode;
  isDefault: boolean;
  variant: AppProductsVariant;
  merchantPublicID: string;
  merchantData: MerchantDetailDto;
  productPricings: Array<ProductPricingVariants>;
  isOnboarding?: boolean;
};

export const ProductDetail = ({
  code,
  isDefault,
  variant,
  merchantData,
  productPricings,
  merchantPublicID,
  isOnboarding,
}: ProductDetailProps) => {
  const { isLoading: isMerchantDataLoading } =
    useGetMerchantQuery(merchantPublicID);
  const userRole = useCurrentUserAccessRole();
  // Orbit L and SoftPOS share one Comgate onboarding. Its progress, terminals and contracts are shown
  // once: on the Orbit L card, or on the SoftPOS card when the merchant has no Orbit L.
  const { data: merchantProducts } =
    useGetMerchantAppProductsQuery(merchantPublicID);
  const isComgateProduct = isComgateProductCode(code);
  const hasOrbitLProduct = !!merchantProducts?.some(
    (p) => p.productDto.code === ProductCode.OrbitL,
  );
  const hasSoftPosProduct = !!merchantProducts?.some(
    (p) => p.productDto.code === ProductCode.SoftPos,
  );
  const isOrbitL =
    isComgateProduct && (code === ProductCode.OrbitL || !hasOrbitLProduct);
  const { data: orbitLStatus } = useGetOrbitLStatusQuery(
    merchantPublicID,
    isComgateProduct,
  );

  if (!isDefault && isMerchantDataLoading) {
    return (
      <Box minHeight={61}>
        <CenteredCircularProgress color="grey" size={35} />
      </Box>
    );
  }

  // Comgate devices settle under their own price list; the status resolves the one this card's devices get.
  const devicePricing =
    code === ProductCode.SoftPos
      ? orbitLStatus?.softPosPricing
      : orbitLStatus?.physicalTerminalPricing;
  const currentPricingID = isComgateProduct
    ? devicePricing?.merchantPricingID
    : merchantData.fees.find((fee) => fee.productCode === code)
        ?.merchantPricingID;
  const canDownloadContracts =
    isOrbitL &&
    ORBIT_L_CONTRACT_DOWNLOAD_ROLES.includes(
      userRole as (typeof ORBIT_L_CONTRACT_DOWNLOAD_ROLES)[number],
    );
  const isHunterRole = isHunter(userRole);
  const isBeforeSubmission =
    orbitLStatus != null && isBeforeComgateSubmission(orbitLStatus);
  const canManageTerminals =
    isOrbitL &&
    canManageOrbitLTerminals({ userRole, isOnboarding, orbitLStatus });
  const isFeeUpdateFormVisible =
    variant === "editable" &&
    isDefault &&
    !!currentPricingID &&
    (!isHunterRole || !!isOnboarding);
  const isCurrentPricingVisible =
    variant !== "editable" || !isDefault || !isFeeUpdateFormVisible;
  const canDeactivateProduct =
    variant === "editable" &&
    !isDefault &&
    (!isHunterRole || !isComgateProduct || isBeforeSubmission);

  return (
    <>
      {isOrbitL && <OrbitLProgress merchantPublicID={merchantPublicID} />}
      {canManageTerminals && orbitLStatus && (
        <OrbitLFacilityTerminals
          merchantPublicID={merchantPublicID}
          facilities={merchantData.facilities}
          financingModel={orbitLStatus.financingModel}
          isTerminalCountLocked={orbitLStatus.isTerminalCountLocked}
          openRequest={orbitLStatus.openTerminalCountRequest}
          hasOrbitLProduct={hasOrbitLProduct}
          hasSoftPosProduct={hasSoftPosProduct}
        />
      )}
      {isCurrentPricingVisible && (
        <CurrentPricingFee
          productCode={code}
          productPricings={productPricings}
          currentPricingID={currentPricingID ?? undefined}
        />
      )}
      {canDownloadContracts && orbitLStatus && (
        <OrbitLContractDownloads
          merchantPublicID={merchantPublicID}
          isLoanContractRequired={orbitLStatus.isLoanContractRequired}
        />
      )}
      {canDeactivateProduct && (
        <DeactivateProductButton
          productCode={code}
          merchantPublicID={merchantPublicID}
        />
      )}
      {isFeeUpdateFormVisible && (
        <ProductFeeUpdateForm
          productCode={code}
          pricings={productPricings}
          value={currentPricingID.toString()}
          merchantPublicID={merchantPublicID}
          currentFees={merchantData?.fees ?? []}
        />
      )}
    </>
  );
};
